import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { CreateTicketRequest, PreparedTicketAttachment, TicketRequest } from '@proto/mypascom';
import { Subscription } from 'src/app/subscription/subscription.model';
import { AttachedFile } from 'src/app/support/support.model';
import { SupportV2Service } from '../../supportv2.service';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { AssetService } from 'src/app/asset.service';
import { CreateTicketResponse_Outcome } from '@proto/hubspotr';
import { ToastService } from 'src/app/toast.service';
import { I18NextService } from 'angular-i18next';

@Component({
  selector: 'app-create-ticket-form-v2',
  templateUrl: './create-ticket-form-v2.component.html',
  standalone: false
})
export class CreateTicketFormV2Component implements OnInit {

  @Input()
  subscription: Subscription;

  public description: string;
  public subject: string;
  public isRequesting = false;
  public isUploading = false;
  public attachedFiles: AttachedFile[] = [];
  public preparedAttachments: PreparedTicketAttachment[] = [];

  constructor(private supportService: SupportV2Service,
    private router: Router,
    private assetService: AssetService,
    private toastService: ToastService,
    private i18Next: I18NextService) { 
  }

  ngOnInit() {
    if (!this.subscription.hasSupport) {
      this.router.navigate(['/subscriptions', this.subscription.appliance_id]);
    }
  }

  get subscriptionName(): string {
    return '[' + this.subscription.appliance_id + '] ' + this.subscription.name;
  }

  back() {
    window.history.back();
  }

  submitTicket() {
    if (!this.description || !this.subject) {
      return;
    }
    this.startAttachmentUpload();
  }

  async startAttachmentUpload() {
    if (this.attachedFiles.length > 0) {
      this.isUploading = true;
      this.preparedAttachments = [];
      try {
        for (let f of this.attachedFiles) {
          const file = f.file;
          const assetData = await firstValueFrom(this.assetService.placeAsset(file.name, file.type));
          await lastValueFrom(this.assetService.upload(assetData.uploadUrl, file));
          const attachment = PreparedTicketAttachment.create({
            assetKey: assetData.assetKey,
            filename: file.name
          });
          this.preparedAttachments.push(attachment);
        }
        this.isUploading = false;
        this.createTicketInHubspot();
      } catch (error) {
        this.isUploading = false;
        this.toastService.error(this.i18Next.t('support_ticket_upload_files_error'), 'error');
      }
    } else {
      this.createTicketInHubspot();
    }
  }

  async createTicketInHubspot() {
    this.isRequesting = true;
    const createTicketRequest = CreateTicketRequest.create({
      applianceId: this.subscription.appliance_id,
      title: this.subject,
      body: this.description,
      attachments: this.preparedAttachments,
    });
    const ticket = TicketRequest.create({
      request: {
        $case: 'createTicketRequest',
        createTicketRequest: createTicketRequest
      }
    });

    try {
      const createTicketResponse = await firstValueFrom(this.supportService.createTicket(ticket));
      this.isRequesting = false;
      const result = createTicketResponse.outcome;

      if (result === CreateTicketResponse_Outcome.OUTCOME_SUCCESS) {
        this.toastService.success(this.i18Next.t('support_ticket_create_success'), 'success');
        this.description = '';
        this.router.navigate(['/supportV2/ticket/', createTicketResponse.threadId]);
      }
      if (result === CreateTicketResponse_Outcome.OUTCOME_ERROR) {
        this.toastService.error(this.i18Next.t('support_ticket_create_error'), 'error');
      }
    } catch (error) {
      this.isRequesting = false;
      this.toastService.error(this.i18Next.t('support_ticket_create_error'), 'error');
    }
  }

  // Sets files from the file-uploader-component -> no actual upload has happened yet
  selectedFilesChanged(attachedFiles: AttachedFile[]) {
    this.attachedFiles = attachedFiles;
  }

  get isDescriptionBlank() {
    return !this.description || this.description.trim().length === 0;
  }

  get isSubjectBlank() {
    return !this.subject || this.subject.trim().length === 0;
  }
}
